import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData,
} from "chart.js";
import { Line } from "react-chartjs-2";
import fetchTimeliveAPI from '../../../../common/fetchTimeliveAPI';
import { parseISO, isBefore, format } from "date-fns";
import moment from 'moment';
import { formatDateWithDateAndMonthName, FormatInvoiceDecimalNumber, formatNumberCustomoriginalapiv2, getValueFromResource } from '../../../../common/timeLiveFunction';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useTranslation } from 'react-i18next';
import noData from "./../../../../assets/image/noDataExist.png"
import { KTIcon } from '../../../../_metronic/helpers';
import { useRuntimeConfig } from '../../../../hooks/useRuntimeConfig';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
const btnIconClass = 'fs-1 text-gray-600';
const AccountProjectForecast = (props: any) => {
    const { AccountProjectId, startDate, endDate } = props;
    const { maxMenuHeight, CurrentCultureDateFormat, cultureInfoName, decimalPlaces, dateInsertionFormat } = useAppSelector((state) => state.globalState);
    const [forecastData, setForecastData] = useState<any[]>([]);
    const [estimate, setEstimate] = useState<number>(0);
    const [initialAxisPoint, setInitialAxisPoint] = useState(0);
    const [finalAxisPoint, setFinalAxisPoint] = useState(0);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
      const config = useRuntimeConfig();

    useEffect(() => {
        const fetchForecastData = async () => {
            try {
                const response = await fetchTimeliveAPI(`api/Forecast/GetAccountProjectForecast/${AccountProjectId}/${moment(startDate).format('YYYY-MM-DD')}/${moment(endDate).format('YYYY-MM-DD')}`, 'Get', '', true);
                const result = await response.json();
                setForecastData(result.Data || []);
                if (result.Data.length > 0) {
                    const lastIndex = result.Data.length - 1;
                    setEstimate(result.Data[0].EstimatedDuration)
                    setInitialAxisPoint(result.Data[0]?.Spent.toFixed(2));
                    setFinalAxisPoint(result.Data[lastIndex]?.Scheduled.toFixed(2));
                } else {
                    setEstimate(0);
                }
            } catch (error) {
                console.error("Error fetching forecast data:", error);
            }
        };
        fetchForecastData();
    }, [startDate, endDate, AccountProjectId]);

    const getAllDatesInRange = (start: string, end: string) => {
        const arr = [];
        const dt = new Date(start);
        const endDt = new Date(end);
        while (dt <= endDt) {
            arr.push(format(dt, 'yyyy-MM-dd'));
            dt.setDate(dt.getDate() + 1);
        }
        return arr;
    };

    const allDatesLabels = getAllDatesInRange(startDate, endDate);

    const chartDataList = allDatesLabels.map(dateStr => {
        const found = forecastData.find(item => item.ForecastDate.startsWith(dateStr));
        if (found) return found;
        return {
            ForecastDate: dateStr,
            Scheduled: null,
            Spent: null,
            ForecastedRemaining: null,
            isEmpty: true
        };
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const spentAndForecastData = chartDataList.map((item) => {
        if (item.Spent === null) return null;
        return item.Spent;
    });

    const scheduledData = chartDataList.map(item => item.Scheduled);
    const totalAllocation = forecastData[0]?.TotalAllocationHours ?? 0;

    const maxDataValue = Math.max(
        ...forecastData.map(i =>
            Math.max(i.Scheduled, i.Spent || 0)
        )
    );
    const hasData = useMemo(() => {
        return forecastData && forecastData.length > 0 && maxDataValue > 0;
    }, [forecastData, maxDataValue]);
    // const yMax = Math.max(totalAllocation, maxDataValue);
    const yMax = Math.max(totalAllocation, maxDataValue, estimate ?? 0) * 1.05;

    const isOverEstimate = (estimate && estimate > 0) && (totalAllocation > estimate || forecastData.some(item => item.Spent > estimate));
    const saturationIndex = chartDataList.findIndex(item => item.Scheduled !== null && item.Scheduled >= (totalAllocation - 0.01));

    const estimateLabelPlugin = useMemo(() => ({
        id: 'estimateLabel',
        afterDatasetsDraw(chart: any) {
            if (!estimate || estimate <= 0) return;

            const { ctx, scales: { x, y } } = chart;
            const yPos = y.getPixelForValue(estimate);
            const xStart = x.left;

            ctx.save();
            ctx.fillStyle = isOverEstimate ? "red" : "#6B778C";
            ctx.font = "bold 12px sans-serif";
            ctx.textBaseline = "bottom";
            ctx.fillText(`${getValueFromResource('Estimate', dispatch, t)}: ${formatNumberCustomoriginalapiv2(cultureInfoName.toString(), estimate, 2)}h`, xStart + 10, yPos - 5);
            ctx.restore();
        }
    }), [estimate, isOverEstimate, startDate, endDate])

    const data: ChartData<"line"> = {
        labels: allDatesLabels,
        datasets: [
            {
                label: "Scheduled",
                data: scheduledData,
                borderColor: '#777777',//"#172b4d",
                backgroundColor: "rgba(99, 110, 114, 0.3)",
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 0,
                spanGaps: true,
            },
            {
                label: "Spent / Forecasted Spent",
                data: spentAndForecastData,
                borderColor: "#0C66E4",
                backgroundColor: "rgba(0, 123, 255, 0.3)",
                tension: 0.4,
                borderWidth: 2,
                pointRadius: 0,
                spanGaps: true,
                segment: {
                    borderColor: (ctx: any) => {
                        const p1Index = ctx.p1DataIndex;
                        if (!chartDataList[p1Index]) return "#0C66E4";

                        const val = chartDataList[p1Index].Spent;

                        if (estimate && estimate > 0 && val > estimate) {
                            return "red";
                        }
                        return "#0C66E4";
                    },
                    borderDash: (ctx: any) => {
                        if (!chartDataList[ctx.p0DataIndex] || !chartDataList[ctx.p0DataIndex].ForecastDate) return [];
                        const dateStr = chartDataList[ctx.p0DataIndex].ForecastDate;
                        const date = parseISO(dateStr);
                        return isBefore(today, date) ? [2, 5] : [];
                    },
                }
            },
            ...(estimate && estimate > 0 ? [{
                label: "Estimate Threshold",
                data: allDatesLabels.map(() => estimate),
                borderColor: isOverEstimate ? "red" : "#A1A1AA",
                backgroundColor: isOverEstimate ? "rgba(239, 68, 68, 0.3)" : "rgba(161, 161, 170, 0.3)",
                tension: 0,
                borderWidth: 1.5,
                pointRadius: 0,
                segment: {
                    borderDash: (ctx: any) => {
                        if (isOverEstimate) return [];
                        if (saturationIndex !== -1 && ctx.p0DataIndex >= saturationIndex) {
                            return [6, 6];
                        }
                        return [];
                    }
                }
            }] : [])
        ]
    }
    const isFirstMonth = moment(startDate).isSame(moment(startDate, 'YYYY-MM-DD'), 'month');
    const dataMinValue = Math.min(
        ...forecastData.map(i => i.Scheduled !== null ? i.Scheduled : Infinity),
        ...forecastData.map(i => i.Spent !== null ? i.Spent : Infinity)
    );

    const yAxisMin = isFirstMonth ? 0 : dataMinValue;
    const options: ChartOptions<"line"> = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index",
            intersect: false
        },
        plugins: {
            title: {
                display: false,
            },
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false as unknown as boolean,
                mode: "index",
                intersect: false,
                external: function (context: any) {
                    const tooltipModel = context.tooltip;
                    let tooltipEl = document.getElementById("chartjs-tooltip");
                    if (!tooltipEl) {
                        tooltipEl = document.createElement("div");
                        tooltipEl.id = "chartjs-tooltip";
                        tooltipEl.style.pointerEvents = "none";
                        tooltipEl.style.position = "absolute";
                        tooltipEl.style.transform = "translate(-50%, 0)";
                        tooltipEl.style.background = "rgba(255,255,255,0.98)";
                        tooltipEl.style.border = "1px solid rgba(0,0,0,0.08)";
                        tooltipEl.style.borderRadius = "8px";
                        tooltipEl.style.padding = "8px 10px";
                        tooltipEl.style.fontSize = "13px";
                        tooltipEl.style.color = "#777777";//"#172b4d";
                        tooltipEl.style.boxShadow = "0 6px 18px rgba(0,0,0,0.08)";
                        tooltipEl.style.whiteSpace = "nowrap";
                        tooltipEl.style.zIndex = "9999";
                        document.body.appendChild(tooltipEl);
                    }
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = "0";
                        return;
                    }
                    const dataPoints = tooltipModel.dataPoints;
                    if (!dataPoints || dataPoints.length === 0) {
                        tooltipEl.style.opacity = "0";
                        return;
                    }
                    const index = dataPoints[0].dataIndex;
                    const item = chartDataList[index];
                    if (!item || item.isEmpty) {
                        tooltipEl.style.opacity = "0";
                        return;
                    }
                    const forecastDate = parseISO(item.ForecastDate);
                    const isFuture = isBefore(today, forecastDate);

                    const header = `<div style="font-weight:600;margin-bottom:6px;font-size:13px;color:#172b4d;">
                        ${formatDateWithDateAndMonthName(item.ForecastDate, cultureInfoName.toString())}</div>`;
                    const lines = [
                        { color: "#777777", text: `${getValueFromResource('Scheduled', dispatch, t)}: ${item.Scheduled}h` },
                        {
                            color: isOverEstimate ? 'red' : "#0C66E4",
                            text: isFuture
                                ? `${getValueFromResource('Forecasted Spend', dispatch, t)}: ${item.Spent}h`
                                : `${getValueFromResource('Spent', dispatch, t)}: ${item.Spent}h`
                        },
                        {
                            color: "#ffcc00",
                            text: isFuture
                                ? `${getValueFromResource('Forecasted Remaining', dispatch, t)}: ${item.ForecastedRemaining}h`
                                : `${getValueFromResource('Remaining', dispatch, t)}: ${item.ForecastedRemaining}h`
                        }
                    ];
                    const body = lines
                        .map(
                            (l) => `
                                    <div style="display:flex;align-items:center;gap:8px;margin:4px 0;">
                                        <span style="width:10px;height:10px;border-radius:50%;display:inline-block;background:${l.color};flex:0 0 10px;"></span>
                                        <span style="font-weight:500">${l.text}</span>
                                    </div>`
                        ).join("");
                    tooltipEl.innerHTML = header + body;
                    const canvasRect = context.chart.canvas.getBoundingClientRect();
                    const caretX = tooltipModel.caretX ?? (canvasRect.width / 2);
                    const caretY = tooltipModel.caretY ?? 0;
                    const pageX = canvasRect.left + window.pageXOffset + caretX;
                    const pageY = canvasRect.top + window.pageYOffset + caretY - 10;
                    tooltipEl.style.left = `${pageX}px`;
                    tooltipEl.style.top = `${pageY}px`;
                    tooltipEl.style.opacity = "1";
                },
            }
        },
        scales: {
            x: {
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: false,
                    maxTicksLimit: 10,
                    align: "inner",
                    callback: function (value, index, ticks) {
                        if (index === 0) {
                            // return moment(startDate).format(CurrentCultureDateFormat.toString());
                            return formatDateWithDateAndMonthName(moment.parseZone(startDate).format(dateInsertionFormat), cultureInfoName.toString())
                        }
                        if (index === ticks.length - 1) {
                            // return moment(endDate).format(CurrentCultureDateFormat.toString());
                            return formatDateWithDateAndMonthName(moment.parseZone(endDate).format(dateInsertionFormat), cultureInfoName.toString())
                        }
                        return "";
                    }
                },
                grid: { display: false, drawTicks: false }
            },
            y: {
                min: 0,
                max: yMax,//finalAxisPoint * 1.05,
                display: true,
                beginAtZero: true,
                afterBuildTicks: function (axis) {
                    const showInitialPoint = initialAxisPoint > (finalAxisPoint * 0.05);
                    axis.ticks = showInitialPoint
                        ? [
                            { value: 0 },
                            { value: initialAxisPoint },
                            { value: finalAxisPoint }
                        ]
                        : [
                            { value: 0 },
                            { value: finalAxisPoint }
                        ];
                },
                ticks: {
                    callback: function (value) {
                        if (value === 0) return `${formatNumberCustomoriginalapiv2(cultureInfoName.toString(), 0, decimalPlaces)}`//"0";
                        if (value === initialAxisPoint && initialAxisPoint > (finalAxisPoint * 0.02)) {
                            //return `${initialAxisPoint}`;
                            return `${formatNumberCustomoriginalapiv2(cultureInfoName.toString(), initialAxisPoint, decimalPlaces)}`
                        }
                        if (value === finalAxisPoint) return `${formatNumberCustomoriginalapiv2(cultureInfoName.toString(), finalAxisPoint, decimalPlaces)}`//`${finalAxisPoint}`;
                        return "";
                    }
                },
                grid: { display: false },
                border: { display: true, width: 1 }
            }
        }
    }

    return (
        <Fragment>
            <div className="p-4 bg-white rounded-2xl shadow-md mt-4">
                <div style={{ height: '400px', width: '100%', position: 'relative' }}>
                    {!hasData && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '20px',
                                textAlign: 'center'
                            }}
                        >
                            <img
                                src={noData}
                                alt="No Record Found"
                                style={{
                                    width: '170px',
                                    opacity: 0.4
                                }}
                            />
                            <div className="d-flex gap-2 align-items-center">
                                <p
                                    style={{
                                        fontSize: '16px',
                                        color: '#a1a5b7',
                                    }}
                                >
                                    {getValueFromResource('No Record Found', dispatch, t)}
                                </p>

                                <a
                                    // href="https://www.livetecs.com/timelivehelp-v13/1/en/topic/forecast"
                                    href={config?.FORECAST_HELP_URL || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-tooltip-id="tooltip-Description"
                                    data-tooltip-content={('Help')}
                                    data-tooltip-place="top"
                                    className="d-flex align-items-center"
                                >
                                    <KTIcon iconName='question-2' className={btnIconClass} />
                                </a>
                            </div>
                        </div>
                    )}
                    <Line key={estimate} options={options} data={data} plugins={[estimateLabelPlugin]} />
                </div>
                {/* <Line options={options} data={data} plugins={[estimateLabelPlugin]} /> */}
                <div className="d-flex justify-content-between ms-4 mt-4">
                    <div>
                        {estimate && estimate > 0 ? (
                            <span className='fw-dark'>
                                {getValueFromResource('Based on the', dispatch, t)} <span className='text-hover-primary cursor-pointer'>{getValueFromResource('current schedule', dispatch, t)}</span>,
                                {getValueFromResource('this project will', dispatch, t)} {isOverEstimate ? 'go over' : 'remain under'} {getValueFromResource('the estimate by', dispatch, t)} <strong>{FormatInvoiceDecimalNumber(cultureInfoName.toString(), Math.abs((estimate) - totalAllocation), 2)} {getValueFromResource('hours', dispatch, t)}</strong>
                            </span>
                        ) : (
                            <span className='fw-dark'>
                                {getValueFromResource('Based on the', dispatch, t)} <span className='text-hover-primary cursor-pointer'>{getValueFromResource('current schedule', dispatch, t)}</span>,
                                {getValueFromResource('total allocation is', dispatch, t)} <strong>{FormatInvoiceDecimalNumber(cultureInfoName.toString(), totalAllocation, 2)} {getValueFromResource('hours', dispatch, t)}</strong> (No Estimate Set).
                            </span>
                        )}
                    </div>
                    <div className="text-sm mb-14 d-flex align-items-center justify-content-end gap-2">
                        <span style={{ color:  '#0C66E4', fontSize: '16px', marginTop: '3px' }}>━━</span> {getValueFromResource('Completed', dispatch, t)}{' '}
                        <span style={{ color:  '#0C66E4', fontWeight: 'bold', fontSize: '18px', letterSpacing: '1px' }}>---</span> {getValueFromResource('Forecasted', dispatch, t)}{' '}
                        <span style={{ color: '#777777', fontSize: '16px', marginTop: '3px' }}>━━</span> {getValueFromResource('Scheduled', dispatch, t)}{' '}
                        <span style={{ color: `${isOverEstimate ? 'red' : '#777777'}`, fontSize: '16px', marginTop: '3px' }}>━━</span> {getValueFromResource('Estimated', dispatch, t)}{' '}
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default AccountProjectForecast;